import { Columns, Settings2, X } from 'lucide-react'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../lib/utils'

export interface ColumnConfig {
    id: string
    label: string
    visible: boolean
}

interface Props {
    columns: ColumnConfig[]
    onChange: (id: string) => void
}

export const ColumnSelector: React.FC<Props> = ({ columns, onChange }) => {
    const [isOpen, setIsOpen] = useState(false)
    // position state replaced by dropdownStyle
    const triggerRef = useRef<HTMLButtonElement>(null)
    const [mounted, setMounted] = useState(false)
    const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({})

    useEffect(() => {
        setMounted(true)
    }, [])

    useEffect(() => {
        const updatePosition = () => {
            if (isOpen && triggerRef.current) {
                const rect = triggerRef.current.getBoundingClientRect()
                const viewportWidth = window.innerWidth
                const viewportHeight = window.innerHeight
                const width = 288 // w-72
                const gap = 8

                // Horizontal Positioning (Right anchored preference)
                let left = rect.right - width
                if (left < 10) left = 10 // Left boundary
                if (left + width > viewportWidth - 10) left = viewportWidth - width - 10 // Right boundary

                // Vertical Positioning (Smart Flip)
                const spaceBelow = viewportHeight - rect.bottom
                const spaceAbove = rect.top
                const dropdownHeightEstimate = 400 // rough max height estimate

                let top: number | undefined
                let bottom: number | undefined
                let maxHeight: number
                let transformOrigin: string

                // Prefer bottom if space allows, or if neither allows fully but bottom has more space
                if (spaceBelow >= dropdownHeightEstimate || spaceBelow >= spaceAbove) {
                    top = rect.bottom + gap
                    maxHeight = Math.min(dropdownHeightEstimate, spaceBelow - 20) // 20px buffer
                    transformOrigin = 'top right'
                } else {
                    // Flip to top
                    bottom = viewportHeight - rect.top + gap
                    maxHeight = Math.min(dropdownHeightEstimate, spaceAbove - 20)
                    transformOrigin = 'bottom right'
                }

                setDropdownStyle({
                    position: 'fixed',
                    left: `${left}px`,
                    top: top !== undefined ? `${top}px` : undefined,
                    bottom: bottom !== undefined ? `${bottom}px` : undefined,
                    maxHeight: `${maxHeight}px`,
                    zIndex: 9999,
                    transformOrigin,
                })
            }
        }

        if (isOpen) {
            updatePosition()
            window.addEventListener('resize', updatePosition)
            window.addEventListener('scroll', updatePosition, true)
        }

        return () => {
            window.removeEventListener('resize', updatePosition)
            window.removeEventListener('scroll', updatePosition, true)
        }
    }, [isOpen])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (!isOpen) return

            if (triggerRef.current && !triggerRef.current.contains(event.target as Node)) {
                // Check if click is inside the portal content
                const target = event.target as HTMLElement
                const dropdown = document.querySelector('.column-selector-dropdown')
                if (dropdown && dropdown.contains(target)) {
                    return
                }
                setIsOpen(false)
            }
        }
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside)
        }
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [isOpen])

    const activeCount = columns.filter((c) => c.visible).length

    return (
        <>
            <button
                ref={triggerRef}
                onClick={() => setIsOpen(!isOpen)}
                className={cn(
                    'group flex items-center gap-2.5 px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all duration-300 active:scale-95 border cursor-pointer',
                    isOpen
                        ? 'bg-slate-800 dark:bg-slate-700 border-slate-700 dark:border-slate-600 text-white shadow-lg shadow-slate-900/20 ring-2 ring-slate-200 dark:ring-slate-600'
                        : 'bg-white/40 dark:bg-slate-800/40 backdrop-blur-md border-white/40 dark:border-slate-700/40 text-slate-600 dark:text-slate-300 hover:border-indigo-200 dark:hover:border-slate-600 hover:text-slate-800 dark:hover:text-slate-100 dark:shadow-slate-900/10 hover:shadow-md ring-1 ring-white/40 dark:ring-slate-700/40',
                )}
            >
                <Settings2
                    size={16}
                    className={cn(
                        'transition-transform duration-500',
                        isOpen ? 'rotate-180 text-emerald-400' : 'group-hover:rotate-90',
                    )}
                />
                <span>Columns</span>
                {activeCount < columns.length && (
                    <span
                        className={cn(
                            'flex items-center justify-center text-[9px] h-5 w-5 rounded-full ml-1 font-black transition-colors',
                            isOpen
                                ? 'bg-emerald-500 text-white'
                                : 'bg-slate-100 text-slate-600 group-hover:bg-slate-200',
                        )}
                    >
                        {activeCount}
                    </span>
                )}
            </button>

            {isOpen &&
                mounted &&
                createPortal(
                    <div
                        className="column-selector-dropdown fixed w-72 bg-white/70 dark:bg-slate-900/80 backdrop-blur-2xl rounded-2xl shadow-2xl shadow-slate-900/10 border border-white/50 dark:border-slate-700/50 overflow-hidden animate-in fade-in zoom-in-95 duration-200 ring-1 ring-white/60 dark:ring-slate-700/50 flex flex-col"
                        style={dropdownStyle}
                    >
                        <div className="px-5 py-4 bg-linear-to-r from-slate-50/80 to-white/80 dark:from-slate-800/80 dark:to-slate-900/80 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center shrink-0">
                            <div>
                                <h4 className="text-xs font-black text-slate-800 dark:text-slate-200 uppercase tracking-widest">
                                    Display Options
                                </h4>
                                <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1 font-medium">
                                    Select columns to show
                                </p>
                            </div>

                            <button
                                onClick={() => setIsOpen(false)}
                                className="h-6 w-6 flex items-center justify-center rounded-full hover:bg-rose-50 dark:hover:bg-rose-900/20 text-slate-400 hover:text-rose-500 transition-all duration-200 cursor-pointer"
                            >
                                <X size={14} strokeWidth={2.5} />
                            </button>
                        </div>

                        <div className="py-2 overflow-y-auto custom-scrollbar p-2 space-y-1 max-h-[60vh] sm:max-h-[400px]">
                            {columns.map((col) => (
                                <label
                                    key={col.id}
                                    className="flex items-center justify-between px-3 py-2.5 rounded-xl hover:bg-slate-50/80 dark:hover:bg-slate-800/80 cursor-pointer group transition-all duration-200 select-none active:scale-[0.98]"
                                >
                                    <div className="flex items-center gap-3 overflow-hidden">
                                        <div className="p-2.5 bg-linear-to-r from-indigo-500 to-indigo-600 rounded-xl shadow-lg shadow-indigo-500/20 group-hover:scale-105 transition-transform duration-300">
                                            <Columns size={18} className="text-white" />
                                        </div>
                                        <span
                                            className={cn(
                                                'text-xs font-bold truncate transition-colors',
                                                col.visible
                                                    ? 'text-slate-700 dark:text-slate-200'
                                                    : 'text-slate-400 dark:text-slate-500',
                                            )}
                                        >
                                            {col.label}
                                        </span>
                                    </div>

                                    {/* IOS Style Toggle */}
                                    <div
                                        className={cn(
                                            'w-9 h-5 rounded-full relative transition-colors duration-300 shadow-inner',
                                            col.visible
                                                ? 'bg-slate-800 dark:bg-emerald-500'
                                                : 'bg-slate-200 dark:bg-slate-700',
                                        )}
                                    >
                                        <div
                                            className={cn(
                                                'absolute top-1 left-1 bg-white h-3 w-3 rounded-full shadow-sm transition-transform duration-300',
                                                col.visible ? 'translate-x-4' : 'translate-x-0',
                                            )}
                                        />
                                    </div>

                                    <input
                                        type="checkbox"
                                        className="hidden"
                                        checked={col.visible}
                                        onChange={() => onChange(col.id)}
                                    />
                                </label>
                            ))}
                        </div>

                        <div className="p-3 bg-white/40 dark:bg-slate-800/40 border-t border-slate-100 dark:border-slate-700 shrink-0 flex justify-between items-center text-[10px] font-semibold text-slate-400 dark:text-slate-500">
                            <span>
                                Visible: <span className="text-slate-700 dark:text-slate-300">{activeCount}</span>
                            </span>
                            <span>Total: {columns.length}</span>
                        </div>
                    </div>,
                    document.body,
                )}
        </>
    )
}
